import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  RefreshCw, ArrowLeft, CheckCircle, UtensilsCrossed, Bike, School,
  Wifi, Clock, MapPin, CircleDot, History, X, Eye, CalendarDays,
  ChevronLeft, ChevronRight, Package, Loader2, Maximize2, Minimize2,
  Check,
} from "lucide-react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { getMyLunchUpdates, getLunchHistory, getDeliveryAgentsByChild } from "@/services/parent";
import { getDeliveryLocation } from "@/services/deliveryAgent";
import { getMediaUrl } from "@/lib/helpers";
import { removeWhiteBg } from "@/lib/removeWhiteBg";

// ─── Types ─────────────────────────────────────────────────────────────────

interface PickupItem {
  Status: string;
  Attachment: string | null;
  Created_At?: string;
  Child_Name?: string[];
}

interface DeliveryItem {
  School_Code: string;
  Branch_Code: string;
  School_Name: string;
  Branch_Name: string;
  Status: string;
  Attachment: string | null;
  Created_At?: string;
  Child_Name?: string[];
}

interface TrackingData {
  Pickup_Data: PickupItem[];
  Delivery_Update: DeliveryItem[];
}
interface ChildItem {
  Child_Name: string;
  Line_item_Id: string;
}

interface DeliveryBoyItem {
  Delivery_Boy: string;
  Child: ChildItem[];
}

interface DayRecord {
  Date: string;
  Day: string;
  Holiday: boolean;
  Festival_Name: string | null;
  Leave: boolean;
  Pickup_Data: PickupItem[];
  Delivery_Update: DeliveryItem[];
}

type StepStatus = "done" | "active" | "pending";

// ─── Helpers ───────────────────────────────────────────────────────────────

function fmt(iso?: string) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function stepColor(s: StepStatus) {
  if (s === "done") return "#22A56C";
  if (s === "active") return "#D4AF37";
  return "#CBD5E1";
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// ─── Live Map ────────────────────────────────────────────────────────────────

const GMAP_LIBS: ("places")[] = ["places"];
const MAP_STYLE = { width: "100%", height: "100%", minHeight: "100%" };
const ICON_SRC = "/delivery-person.jpg";

function LiveMap({ selectedBoy }: { selectedBoy: string }) {
  const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "").replace(/^['"]|['"]$/g, "");
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: apiKey, libraries: GMAP_LIBS });


  const [location, setLocation] = useState<{ lat: number; lng: number; updatedAt: string } | null>(null);
  const [iconUrl, setIconUrl] = useState(ICON_SRC);
  const [maximized, setMaximized] = useState(false);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fetchingRef = useRef(false);
  const mapRef = useRef<google.maps.Map | null>(null);
  const deliveryUsername = selectedBoy;

  useEffect(() => { removeWhiteBg(ICON_SRC).then(setIconUrl); }, []);

  const fetchLoc = useCallback(async () => {
    if (!deliveryUsername || fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const res = await getDeliveryLocation(deliveryUsername);
      const d = res.data as any;
      const loc = Array.isArray(d) ? d[0] : d;
      if (loc?.latitude && loc?.longitude) {
        const lat = Number(loc.latitude), lng = Number(loc.longitude);
        setLocation(prev => {
          if (prev && prev.lat === lat && prev.lng === lng) return prev;
          mapRef.current?.panTo({ lat, lng });
          return { lat, lng, updatedAt: loc.updated_at || new Date().toISOString() };
        });
      }
    } catch { }
    finally { fetchingRef.current = false; }
  }, [deliveryUsername]);

  useEffect(() => {
    if (!deliveryUsername) return;
    fetchLoc();
    intervalRef.current = setInterval(fetchLoc, 2000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [deliveryUsername, fetchLoc]);

  const icon = isLoaded && window.google
    ? { url: iconUrl, scaledSize: new window.google.maps.Size(44, 44), anchor: new window.google.maps.Point(22, 44) }
    : undefined;

  const mapContent = (full: boolean) => (
    <>
      {isLoaded && location ? (
        <GoogleMap
          mapContainerStyle={MAP_STYLE}
          center={location}
          zoom={full ? 16 : 15}
          onLoad={map => { mapRef.current = map; }}
          options={{
            mapTypeControl: false, fullscreenControl: false,
            streetViewControl: false, zoomControl: full,
            gestureHandling: "cooperative",
            styles: [{ featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }],
          }}
        >
          <Marker position={location} icon={icon} />
        </GoogleMap>
      ) : (
        <div className="absolute inset-0 flex flex-col justify-evenly py-3 px-2">
          {Array.from({ length: 5 }).map((_, r) => (
            <div key={r} className="flex justify-evenly">
              {Array.from({ length: 12 }).map((__, c) => (
                <div key={c} className="w-[2.5px] h-[2.5px] rounded-full bg-white/10" />
              ))}
            </div>
          ))}
          <div className="absolute inset-0 flex items-center justify-center">
            {deliveryUsername
              ? <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
              : <p className="text-[10px] text-white/40 font-semibold">No delivery partner assigned</p>
            }
          </div>
        </div>
      )}

      {/* Username badge */}
      {deliveryUsername && (
        <div className="absolute bottom-2.5 left-3 flex items-center gap-1.5 bg-black/60 backdrop-blur-sm px-2.5 py-1 rounded-full">
          <div className="w-1.5 h-1.5 rounded-full bg-[#22A56C] animate-pulse" />
          <span className="text-[9px] font-extrabold text-white tracking-widest capitalize">{deliveryUsername}</span>
        </div>
      )}

      {/* Last updated */}
      {location && (
        <div className="absolute bottom-2.5 right-3 flex items-center gap-1 bg-black/60 backdrop-blur-sm px-2.5 py-1 rounded-full">
          <Clock className="w-3 h-3 text-[#D4AF37]" />
          <span className="text-[9px] font-bold text-[#D4AF37]">
            {new Date(location.updatedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </span>
        </div>
      )}

      {/* Maximize / minimize button */}
      <button
        onClick={() => setMaximized(m => !m)}
        className="absolute top-2.5 right-2.5 w-7 h-7 flex items-center justify-center bg-black/60 backdrop-blur-sm rounded-lg hover:bg-black/80 transition-colors"
        title={full ? "Minimize map" : "Maximize map"}
      >
        {full
          ? <Minimize2 className="w-3.5 h-3.5 text-white" />
          : <Maximize2 className="w-3.5 h-3.5 text-white" />
        }
      </button>
    </>
  );

  return (
    <>
      {/* Mini map (always in-page) */}
      <div className="relative h-44 bg-[#1B2748] rounded-2xl overflow-hidden mx-4 mt-4 shadow-md">
        {mapContent(false)}
      </div>

      {/* Fullscreen overlay */}
      {maximized && (
        <div className="fixed inset-0 z-50 bg-[#1B2748]">
          <div className="relative w-full h-full">
            {mapContent(true)}
          </div>
        </div>
      )}
    </>
  );
}

// ─── Progress Bar ───────────────────────────────────────────────────────────

function ProgressBar({ hasPickup, hasDelivery }: { hasPickup: boolean; hasDelivery: boolean }) {
  const steps = [
    { label: "Ready", Icon: UtensilsCrossed, done: true },
    { label: "Pickup", Icon: Bike, done: hasPickup },
    { label: "Delivery", Icon: School, done: hasDelivery },
  ];
  return (
    <div className="flex items-center mx-4 mt-3.5 bg-white rounded-2xl px-4 py-3.5 shadow-sm border border-gray-100">
      {steps.map((s, i) => (
        <div key={s.label} className="flex items-center flex-1">
          <div className="flex flex-col items-center gap-1.5 flex-1">
            <div className={`w-9 h-9 rounded-[10px] flex items-center justify-center ${s.done ? "bg-[#22A56C]/10" : "bg-slate-100"}`}>
              {s.done ? <CheckCircle className="w-[18px] h-[18px] text-[#22A56C]" /> : <s.Icon className="w-[18px] h-[18px] text-slate-400" />}
            </div>
            <span className={`text-[10px] font-bold tracking-[0.2px] ${s.done ? "text-[#1A1F3C]" : "text-slate-400"}`}>{s.label}</span>
          </div>
          {i < steps.length - 1 && (
            <div className={`h-0.5 w-6 rounded mx-1 flex-shrink-0 ${steps[i + 1].done ? "bg-[#22A56C]" : "bg-slate-200"}`} />
          )}
        </div>
      ))}
    </div>
  );
}
interface StepCardProps {
  step: number;
  status: StepStatus;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: string;
  subtitle: string | false;
  badgeText?: string;
  badgeColor?: string;
  attachment?: string | null;
  attachments?: string[];
  items?: {
    image?: string | null;
    children: string[];
    time?: string;
  }[];
  isLast?: boolean;
}

function StepCard({
  step,
  status,
  Icon,
  title,
  subtitle,
  badgeText,
  badgeColor,
  attachment,
  attachments = [],
  items = [],
  isLast,
}: StepCardProps) {
  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const color = stepColor(status);
  const isDone = status === "done";
  const isActive = status === "active";

  const bc = badgeColor ?? color;

  const imageUrls = (
    attachments.length
      ? attachments
      : attachment
        ? [attachment]
        : []
  )
    .map((a) => getMediaUrl(a))
    .filter(Boolean) as string[];

  const cardBorder = isDone
    ? "border-[#22A56C]/20 bg-[#FCFFFE]"
    : isActive
      ? "border-[#D4AF37]/30 bg-[#FFFEF7]"
      : "border-gray-100 bg-white";

  return (
    <>
      <div className="flex gap-2.5">
        {/* Timeline */}
        <div className="flex flex-col items-center w-8 flex-shrink-0">
          <div
            className={`w-8 h-8 rounded-full border-2 flex items-center justify-center z-10 bg-white ${isDone ? "border-[#22A56C] bg-[#22A56C]" : ""
              }`}
            style={{ borderColor: color }}
          >
            {isDone ? (
              <Check className="w-6 h-6 text-green-500" />
            ) : isActive ? (
              <Icon
                className="w-3.5 h-3.5"
                style={{ color } as React.CSSProperties}
              />
            ) : (
              <span
                className="text-[13px] font-extrabold"
                style={{ color }}
              >
                {step}
              </span>
            )}
          </div>

          {!isLast && (
            <div
              className={`flex-1 w-0.5 min-h-5 my-1 rounded ${isDone ? "bg-[#22A56C]" : "bg-slate-200"
                }`}
            />
          )}
        </div>

        {/* Card */}
        <div
          className={`flex-1 mb-2.5 rounded-2xl border overflow-hidden shadow-sm ${cardBorder}`}
        >
          {/* Header */}
          <button
            onClick={() =>
              items.length > 0 && setOpen((p) => !p)
            }
            className="w-full flex items-center gap-2.5 p-3 text-left"
          >
            {/* Icon */}
            <div
              className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${color}1A` }}
            >
              <Icon
                className="w-[17px] h-[17px]"
                style={{ color } as React.CSSProperties}
              />
            </div>

            {/* Title */}
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-bold text-[#1A1F3C] leading-tight">
                {title}
              </p>

              {subtitle && (
                <p className="text-[10px] text-gray-500 leading-snug mt-0.5">
                  {subtitle}
                </p>
              )}
            </div>

            {/* Image Badge */}
            {imageUrls.length > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImage(imageUrls[0]);
                }}
                className="relative flex-shrink-0"
              >
                <img
                  src={imageUrls[0]}
                  alt=""
                  className="w-12 h-12 rounded-xl object-cover"
                />

                <div className="absolute inset-0 bg-black/35 rounded-xl flex items-center justify-center">
                  <Eye className="w-4 h-4 text-white" />
                </div>

                {imageUrls.length > 1 && (
                  <div className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#1A1F3C] text-white text-[9px] font-bold flex items-center justify-center">
                    +{imageUrls.length}
                  </div>
                )}
              </button>
            )}

            {/* Badge */}
            {badgeText && (
              <div
                className="flex items-center gap-1 px-2 py-1 rounded-full border flex-shrink-0"
                style={{
                  backgroundColor: `${bc}18`,
                  borderColor: `${bc}40`,
                }}
              >
                <div
                  className="w-[5px] h-[5px] rounded-full flex-shrink-0"
                  style={{ backgroundColor: bc }}
                />

                <span
                  className="text-[9px] font-extrabold tracking-[0.3px]"
                  style={{ color: bc }}
                >
                  {badgeText}
                </span>
              </div>
            )}
          </button>

          {/* Expanded Children */}
          {items.length > 0 && (
            <div
              className={`transition-all duration-300 overflow-hidden ${open ? "max-h-[500px] px-3 pb-3" : "max-h-0"
                }`}
            >
              <div className="space-y-2 pt-1">
                {items.map((item, idx) => {
                  const img = getMediaUrl(item.image);

                  return (
                    <div
                      key={idx}
                      className="flex items-center justify-between gap-3 bg-gray-50 border border-gray-100 rounded-xl px-3 py-2"
                    >
                      {/* Left */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap gap-1">
                          {item.children.map((child, childIdx) => (
                            <span
                              key={childIdx}
                              className="px-2 py-1 rounded-full bg-white border border-gray-200 text-[10px] font-medium text-gray-700"
                            >
                              {child}
                            </span>
                          ))}
                        </div>

                        {item.time && (
                          <p className="text-[10px] text-gray-400 mt-1">
                            {fmt(item.time)}
                          </p>
                        )}
                      </div>

                      {/* Image */}
                      {img && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedImage(img);
                          }}
                          className="relative flex-shrink-0"
                        >
                          <img
                            src={img}
                            alt=""
                            className="w-12 h-12 rounded-xl object-cover"
                          />

                          <div className="absolute inset-0 bg-black/35 rounded-xl flex items-center justify-center">
                            <Eye className="w-4 h-4 text-white" />
                          </div>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fullscreen Image */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-[999] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center"
          >
            <X className="w-5 h-5 text-white" />
          </button>

          <img
            src={selectedImage}
            alt="Preview"
            className="max-w-full max-h-[90vh] rounded-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}

function MediaModal({ urls, onClose }: { urls: string[]; onClose: () => void }) {
  const [idx, setIdx] = useState(0);
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-10 right-0 text-white/70 hover:text-white">
          <X className="w-6 h-6" />
        </button>
        <img src={urls[idx]} alt="Proof" className="w-full rounded-2xl shadow-2xl object-cover max-h-[70vh]" />
        {urls.length > 1 && (
          <div className="flex items-center justify-center gap-3 mt-3">
            <button onClick={() => setIdx(i => Math.max(0, i - 1))} disabled={idx === 0} className="p-2 bg-white/20 rounded-full disabled:opacity-30">
              <ChevronLeft className="w-4 h-4 text-white" />
            </button>
            <span className="text-white text-xs font-semibold">{idx + 1}/{urls.length}</span>
            <button onClick={() => setIdx(i => Math.min(urls.length - 1, i + 1))} disabled={idx === urls.length - 1} className="p-2 bg-white/20 rounded-full disabled:opacity-30">
              <ChevronRight className="w-4 h-4 text-white" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Day Detail Sheet ───────────────────────────────────────────────────────

function DayDetail({ day, onClose }: { day: DayRecord; onClose: () => void }) {
  const [mediaUrls, setMediaUrls] = useState<string[] | null>(null);
  const hasActivity = day.Pickup_Data.length > 0 || day.Delivery_Update.length > 0;
  const allMedia = [
    ...day.Pickup_Data.map(p => getMediaUrl(p.Attachment)).filter(Boolean) as string[],
    ...day.Delivery_Update.map(d => getMediaUrl(d.Attachment)).filter(Boolean) as string[],
  ];
  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center p-4" onClick={onClose}>
      {/* Backdrop — covers only the right panel */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      {/* Centered modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm flex flex-col overflow-hidden" style={{ maxHeight: '85%' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-[#1A1F3C] flex-shrink-0">
          <div>
            <p className="text-white font-bold text-sm">
              {new Date(day.Date + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
            {day.Holiday && day.Festival_Name && (
              <p className="text-[#D4AF37] text-[10px] font-semibold mt-0.5">{day.Festival_Name}</p>
            )}
          </div>
          <button onClick={onClose} className="w-7 h-7 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1 px-4 py-3 space-y-3">
          <div className="flex flex-wrap gap-2">
            {day.Holiday && (
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                {day.Festival_Name ? `Holiday · ${day.Festival_Name}` : "Holiday"}
              </span>
            )}
            {day.Leave && (
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-brand-100 text-brand-700 border border-brand-200">On Leave</span>
            )}
          </div>
          {!hasActivity && !day.Holiday && !day.Leave && (
            <p className="text-center text-gray-400 text-sm py-6">No activity on this day.</p>
          )}

          {day.Pickup_Data.length > 0 && (
            <div className="space-y-2">
              {/* Heading */}
              <div className="flex items-center gap-2 px-1">
                <div className="w-7 h-7 rounded-lg bg-[#22A56C]/12 flex items-center justify-center">
                  <Bike className="w-4 h-4 text-[#22A56C]" />
                </div>

                <div>
                  <p className="text-[11px] font-extrabold text-[#1A1F3C] tracking-wide">
                    Pickup
                  </p>

                  <p className="text-[9px] text-gray-400 font-medium">
                    {day.Pickup_Data.length} Updates
                  </p>
                </div>
              </div>

              {/* Items */}
              {day.Pickup_Data.map((p, i) => {
                const imageUrl = getMediaUrl(p.Attachment);

                return (
                  <div
                    key={i}
                    className="flex items-center justify-between gap-3 bg-[#22A56C]/8 border border-[#22A56C]/15 rounded-xl px-3 py-2.5"
                  >
                    {/* Left */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap gap-1">
                        {[...new Set(p.Child_Name || [])].map((child, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 rounded-full bg-white border border-[#22A56C]/10 text-[10px] font-semibold text-[#1A1F3C]"
                          >
                            {child}
                          </span>
                        ))}
                      </div>

                      {p.Created_At && (
                        <p className="text-[10px] text-gray-400 mt-1 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {fmt(p.Created_At)}
                        </p>
                      )}
                    </div>

                    {/* Image */}
                    {imageUrl && (
                      <button
                        onClick={() => setMediaUrls([imageUrl])}
                        className="relative flex-shrink-0"
                      >
                        <img
                          src={imageUrl}
                          alt=""
                          className="w-12 h-12 rounded-xl object-cover"
                        />

                        <div className="absolute inset-0 bg-black/35 rounded-xl flex items-center justify-center">
                          <Eye className="w-4 h-4 text-white" />
                        </div>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {day.Delivery_Update.length > 0 && (
            <div className="space-y-2">
              {/* Heading */}
              <div className="flex items-center gap-2 px-1">
                <div className="w-7 h-7 rounded-lg bg-brand-100 flex items-center justify-center">
                  <School className="w-4 h-4 text-brand-500" />
                </div>

                <div>
                  <p className="text-[11px] font-extrabold text-[#1A1F3C] tracking-wide">
                    Delivery
                  </p>

                  <p className="text-[9px] text-gray-400 font-medium">
                    {day.Delivery_Update.length} Updates
                  </p>
                </div>
              </div>

              {/* Items */}
              {day.Delivery_Update.map((d, i) => {
                const imageUrl = getMediaUrl(d.Attachment);

                return (
                  <div
                    key={i}
                    className="flex items-center justify-between gap-3 bg-brand-50/60 border border-brand-100 rounded-xl px-3 py-2.5"
                  >
                    {/* Left */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap gap-1">
                        <span className="px-2 py-0.5 rounded-full bg-white border border-brand-100 text-[10px] font-semibold text-[#1A1F3C]">
                          {d.School_Name}
                        </span>

                        <span className="px-2 py-0.5 rounded-full bg-white border border-brand-100 text-[10px] font-semibold text-gray-500">
                          {d.Branch_Name}
                        </span>
                      </div>

                      {d.Created_At && (
                        <p className="text-[10px] text-gray-400 mt-1 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {fmt(d.Created_At)}
                        </p>
                      )}
                    </div>

                    {/* Image */}
                    {imageUrl && (
                      <button
                        onClick={() => setMediaUrls([imageUrl])}
                        className="relative flex-shrink-0"
                      >
                        <img
                          src={imageUrl}
                          alt=""
                          className="w-12 h-12 rounded-xl object-cover"
                        />

                        <div className="absolute inset-0 bg-black/35 rounded-xl flex items-center justify-center">
                          <Eye className="w-4 h-4 text-white" />
                        </div>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {allMedia.length > 1 && (
            <button onClick={() => setMediaUrls(allMedia)} className="w-full flex items-center justify-center gap-2 py-2.5 bg-brand-50 text-brand-700 rounded-xl text-xs font-bold border border-brand-100 hover:bg-brand-100 transition-colors">
              <Eye className="w-3.5 h-3.5" />
              View All Photos ({allMedia.length})
            </button>
          )}
          <div className="h-4" />
        </div>
      </div>
      {mediaUrls && <MediaModal urls={mediaUrls} onClose={() => setMediaUrls(null)} />}
    </div>
  );
}

// ─── History Calendar ───────────────────────────────────────────────────────

function HistoryCalendar({ selectedDate, onDaySelect }: { selectedDate: string | null; onDaySelect: (r: DayRecord | null) => void }) {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [history, setHistory] = useState<DayRecord[] | null>(null);
  const [loading, setLoading] = useState(false);
  const yearOptions = Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i);

  const handleLoad = useCallback(async (m = month, y = year) => {
    setLoading(true); onDaySelect(null);
    try {
      const res = await getLunchHistory(m, y);
      const arr = (res.data as any)?.data ?? (Array.isArray(res.data) ? res.data : []);
      setHistory(arr);
    } catch { setHistory([]); } finally { setLoading(false); }
  }, [month, year]);

  useEffect(() => { handleLoad(now.getMonth() + 1, now.getFullYear()); }, []);

  const calendarDays = (() => {
    if (!history) return null;
    const firstDay = new Date(year, month - 1, 1).getDay();
    const daysInMonth = new Date(year, month, 0).getDate();
    const map: Record<string, DayRecord> = {};
    history.forEach(d => { map[d.Date] = d; });
    const cells: (DayRecord | null)[] = Array(firstDay).fill(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const iso = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      cells.push(map[iso] ?? { Date: iso, Day: '', Holiday: false, Festival_Name: null, Leave: false, Pickup_Data: [], Delivery_Update: [] });
    }
    return cells;
  })();

  return (
    <div className="flex flex-col">
      {/* Filter bar */}
      <div className="bg-white rounded-2xl mx-4 mt-3 p-4 shadow-sm border border-gray-100">
        <div className="flex items-center gap-2 mb-3">
          <CalendarDays className="w-4 h-4 text-[#D4AF37]" />
          <p className="font-bold text-[#1A1F3C] text-sm">Delivery History</p>
        </div>
        <div className="flex gap-2">
          <select value={month} onChange={e => setMonth(Number(e.target.value))}
            className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4AF37] bg-gray-50 font-medium text-gray-700">
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <select value={year} onChange={e => setYear(Number(e.target.value))}
            className="w-24 px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4AF37] bg-gray-50 font-medium text-gray-700">
            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={() => handleLoad()} disabled={loading}
            className="px-4 py-2 bg-[#1A1F3C] text-white text-sm font-bold rounded-xl hover:bg-[#252d4a] transition-colors disabled:opacity-60 flex items-center gap-1.5">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
            Go
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-10 gap-2">
          <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Loading history…</p>
        </div>
      )}

      {!loading && calendarDays && (
        <div className="mx-4 mt-3 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {/* Month header */}
          <div className="bg-gradient-to-r from-[#1A1F3C] to-[#252d4a] px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-white font-extrabold text-sm tracking-wide">{MONTHS[month - 1]} {year}</p>
              <span className="text-[9px] font-bold text-white/40 uppercase tracking-widest">Delivery Calendar</span>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="flex items-center gap-1.5 text-[9px] font-bold text-white/70"><span className="w-3 h-3 rounded-sm bg-sky-300 inline-block" />Today</span>
              <span className="flex items-center gap-1.5 text-[9px] font-bold text-white/70"><span className="w-3 h-3 rounded-sm bg-amber-300 inline-block" />Pickup only</span>
              <span className="flex items-center gap-1.5 text-[9px] font-bold text-white/70"><span className="w-3 h-3 rounded-sm bg-emerald-300 inline-block" />Delivered</span>
              <span className="flex items-center gap-1.5 text-[9px] font-bold text-white/70"><span className="text-sm">🎉</span>Holiday</span>
              <span className="flex items-center gap-1.5 text-[9px] font-bold text-white/70"><span className="text-sm">🏖️</span>Leave</span>
            </div>
          </div>
          {/* Day labels */}
          <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-100">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map(d => (
              <div key={d} className="text-center py-2 text-[10px] font-bold text-gray-400">{d}</div>
            ))}
          </div>
          {/* Day cells */}
          <div className="grid grid-cols-7 gap-[1px] bg-gray-100">
            {calendarDays.map((cell, i) => {
              if (!cell) return <div key={i} className="bg-white min-h-[60px]" />;
              const dayNum = Number(cell.Date.split('-')[2]);
              const todayIso = new Date().toISOString().split('T')[0];
              const isToday = cell.Date === todayIso;
              const isSelected = cell.Date === selectedDate;
              const hasDelivery = cell.Delivery_Update.length > 0;
              const hasPickup = cell.Pickup_Data.length > 0;
              const hasActivity = hasDelivery || hasPickup;
              const imgSrc = hasActivity ? "/lunchbox.jpg" : cell.Holiday ? null : cell.Leave ? null : "/truck.jpg";
              const fallbackEmoji = cell.Holiday ? "🎉" : cell.Leave ? "🏖️" : null;
              // Pickup done but delivery not recorded yet (past days only)
              const pickupOnlyPast = !isToday && hasPickup && !hasDelivery;
              const cellBg = isSelected
                ? "bg-gradient-to-b from-[#1A1F3C] to-[#252d4a] shadow-inner"
                : isToday
                  ? "bg-gradient-to-b from-sky-100 to-blue-100 ring-2 ring-inset ring-sky-400"
                  : pickupOnlyPast
                    ? "bg-gradient-to-b from-amber-100 to-orange-100 hover:from-amber-150 hover:to-orange-150"
                    : hasActivity
                      ? "bg-gradient-to-b from-emerald-50/80 to-green-50/80 hover:from-emerald-100 hover:to-green-100"
                      : cell.Holiday
                        ? "bg-rose-50/70 hover:bg-rose-100/70"
                        : cell.Leave
                          ? "bg-brand-50/70 hover:bg-brand-100/70"
                          : "bg-white hover:bg-slate-50";
              return (
                <button key={i} onClick={() => onDaySelect(isSelected ? null : cell)}
                  className={`p-1 flex flex-col items-center justify-between min-h-[60px] w-full transition-all duration-150 ${cellBg}`}>
                  {/* Top: date number + dots */}
                  <div className="flex flex-col items-center w-full">
                    <span className={`text-[13px] font-extrabold leading-none mt-0.5 ${isSelected ? "text-[#D4AF37]"
                      : isToday ? "text-sky-600"
                        : pickupOnlyPast ? "text-amber-700"
                          : "text-gray-700"
                      }`}>
                      {dayNum}
                    </span>
                    {isToday && !isSelected && <div className="w-3 h-[2px] rounded-full bg-sky-500 mt-0.5" />}
                  </div>
                  {/* Bottom: image / emoji */}
                  <div className="flex flex-col items-center pb-0.5">
                    {imgSrc
                      ? <img src={imgSrc} alt="" className="w-7 h-7 object-contain" />
                      : fallbackEmoji
                        ? <span className="text-xl leading-none">{fallbackEmoji}</span>
                        : null
                    }
                    <div className="flex gap-[3px] mt-0.5">
                      {hasDelivery && <span className="w-[4px] h-[4px] rounded-full bg-[#22A56C]" />}
                      {hasPickup && <span className="w-[4px] h-[4px] rounded-full bg-[#D4AF37]" />}
                      {cell.Holiday && <span className="w-[4px] h-[4px] rounded-full bg-red-400" />}
                      {cell.Leave && <span className="w-[4px] h-[4px] rounded-full bg-brand-400" />}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {!loading && calendarDays && history?.length === 0 && (
        <div className="text-center py-10 text-gray-400">
          <CalendarDays className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No data for this month.</p>
        </div>
      )}

      {!loading && !calendarDays && (
        <div className="flex flex-col items-center justify-center py-14 gap-3 px-6">
          <span className="text-5xl">📦</span>
          <p className="text-[#1A1F3C] font-bold text-center text-sm">Select a month & year</p>
          <p className="text-xs text-gray-400 text-center">Choose above and tap Go to load your delivery history.</p>
        </div>
      )}
      <div className="h-6" />
    </div>
  );
}

// ─── Error & Empty States ───────────────────────────────────────────────────

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center pt-16 gap-3 px-8">
      <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mb-1">
        <Wifi className="w-10 h-10 text-slate-400" />
      </div>
      <p className="text-[17px] font-bold text-[#1A1F3C] text-center">Couldn't Load Tracking</p>
      <p className="text-xs text-slate-400 text-center leading-relaxed">Check your connection and try again</p>
      <button onClick={onRetry} className="flex items-center gap-2 px-6 py-2.5 bg-[#D4AF37] rounded-xl text-[13px] font-bold text-[#1A1F3C] mt-2 hover:bg-[#c9a52e] transition-colors shadow-sm">
        <RefreshCw className="w-4 h-4" />Try Again
      </button>
    </div>
  );
}

function NotStartedState() {
  return (
    <div className="flex flex-col items-center justify-center pt-16 gap-3 px-8">
      <div className="w-20 h-20 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-1">
        <UtensilsCrossed className="w-10 h-10 text-[#D4AF37]" />
      </div>
      <p className="text-[17px] font-bold text-[#1A1F3C] text-center">No Delivery Today Yet</p>
      <p className="text-xs text-slate-400 text-center leading-relaxed">
        Once you mark lunch as ready from the dashboard,<br />you can track the delivery progress here.
      </p>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export default function LunchTrackingView() {
  const navigate = useNavigate();
  const [data, setData] = useState<TrackingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);
  const [showHistory, setShowHistory] = useState(true);
  const [selectedDay, setSelectedDay] = useState<DayRecord | null>(null);
  const [deliveryBoys, setDeliveryBoys] = useState<DeliveryBoyItem[]>([]);
  const [selectedBoy, setSelectedBoy] = useState<string>("");
  const [children, setChildren] = useState<ChildItem[]>([]);

  useEffect(() => {
    loadDeliveryBoys();
  }, []);

  const handleSelectBoy = (value: string) => {
    setSelectedBoy(value);

    const selected = deliveryBoys.find(
      (item) => item.Delivery_Boy === value
    );

    setChildren(selected?.Child || []);
  };

  const loadDeliveryBoys = async () => {
    try {
      const res = await getDeliveryAgentsByChild();

      const data: DeliveryBoyItem[] = Array.isArray(res.data)
        ? res.data
        : [];

      setDeliveryBoys(data);

      if (data.length > 0) {
        setSelectedBoy(data[0].Delivery_Boy);
        setChildren(data[0].Child || []);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(false);
    try {
      const { data } = await getMyLunchUpdates();
      if (data) setData(((data as any).data?.[0] ?? data) as TrackingData);
      else setError(true);
    } catch { setError(true); } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData(true);
    setRefreshing(false);
  };

  const hasPickup = Array.isArray(data?.Pickup_Data) && data!.Pickup_Data.length > 0;
  const hasDelivery = Array.isArray(data?.Delivery_Update) && data!.Delivery_Update.length > 0;
  const deliveryStatus: StepStatus = hasDelivery ? "done" : hasPickup ? "active" : "pending";

  // Status labels (mirrors RN trackingTitle / eyebrow / trackingSub)
  const statusEyebrow = hasDelivery ? "DELIVERED" : hasPickup ? "DELIVERY IN PROGRESS" : data ? "AWAITING PICKUP" : null;
  const statusSub = hasDelivery ? "Lunch arrived safely at school"
    : hasPickup ? "Delivery partner is heading to school"
      : data ? "Waiting for delivery partner to arrive"
        : null;

  return (
    <div className="h-screen bg-[#F0F2FF] flex flex-col overflow-hidden">
      {/* Hero Bar */}
      <div className="relative bg-[#1A1F3C] px-5 pt-5 pb-5 flex items-center gap-3 rounded-b-3xl overflow-hidden shadow-xl flex-shrink-0">
        <div className="absolute w-40 h-40 rounded-full bg-white/[0.06] -right-12 -top-12 pointer-events-none" />
        <div className="absolute w-20 h-20 rounded-full bg-white/[0.04] -left-6 -bottom-6 pointer-events-none" />
        <button onClick={() => navigate("/dashboard")} className="w-9 h-9 rounded-[11px] bg-white/10 flex items-center justify-center flex-shrink-0 hover:bg-white/20 transition-colors">
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
        <div className="flex-1">
          <h1 className="text-[18px] font-extrabold text-white tracking-[0.2px]">Track Delivery</h1>
          {statusEyebrow ? (
            <div className="flex flex-col mt-0.5">
              <span className="text-[9px] font-bold tracking-[1.6px] text-[#D4AF37]">{statusEyebrow}</span>
              <span className="text-[10px] text-white/50">{statusSub}</span>
            </div>
          ) : (
            <p className="text-[10px] text-white/50 mt-0.5">
              {showHistory ? "Today's status + Delivery history" : "Real-time status of today's lunch"}
            </p>
          )}
        </div>
        {/* History toggle */}
        <button
          onClick={() => setShowHistory(p => !p)}
          className={`w-9 h-9 rounded-[11px] flex items-center justify-center flex-shrink-0 transition-colors ${showHistory ? "bg-[#D4AF37]" : "bg-white/10 hover:bg-white/20"}`}
          title="Delivery History"
        >
          <History className={`w-[18px] h-[18px] ${showHistory ? "text-[#1A1F3C]" : "text-white"}`} />
        </button>
        {/* Refresh (only in today mode) */}
        {!showHistory && (
          <button onClick={handleRefresh} disabled={refreshing || loading} className="w-9 h-9 rounded-[11px] bg-[#D4AF37] flex items-center justify-center flex-shrink-0 hover:bg-[#c9a52e] transition-colors disabled:opacity-60">
            <RefreshCw className={`w-[18px] h-[18px] text-[#1A1F3C] ${refreshing ? "animate-spin" : ""}`} />
          </button>
        )}
      </div>

      {showHistory ? (
        /* ─── Split view: Today 50% + History 50% ─── */
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          {/* Today panel — full tracking UI */}
          <div className="md:w-1/2 overflow-y-auto md:border-r border-gray-200">
            <div className="mx-4 mt-4 bg-white rounded-xl shadow p-4">
              <select
                value={selectedBoy}
                onChange={(e) => handleSelectBoy(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
              >
                <option value="">Select Delivery Boy</option>

                {deliveryBoys.map((item, index) => (
                  <option key={index} value={item.Delivery_Boy}>
                    {item.Delivery_Boy} ({item.Child?.slice(0, 3).map((c) => c.Child_Name).join(", ")})
                  </option>
                ))}
              </select>
            </div>
            {loading ? (
              <div className="flex flex-col items-center justify-center pt-16 gap-3">
                <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-slate-500">Fetching tracking info…</p>
              </div>
            ) : error ? (
              <ErrorState onRetry={() => loadData()} />
            ) : !data ? (
              <NotStartedState />
            ) : (
              <>
                <LiveMap selectedBoy={selectedBoy} />
                <ProgressBar hasPickup={hasPickup} hasDelivery={hasDelivery} />
                <div className="px-4 pt-4 pb-6">
                  <StepCard step={1} status="done" Icon={UtensilsCrossed}
                    title={hasPickup ? 'Lunch Marked Ready' : 'Mark Lunch as Ready'}
                    subtitle={hasPickup ? "You notified the delivery team" : false}
                    badgeText={hasPickup ? "Prepared" : 'Preparing'} badgeColor="#22A56C" />
                  {hasPickup ? (
                    <StepCard
                      step={2}
                      status="done"
                      Icon={Bike}
                      title="Pickup Done"
                      subtitle="Agent has collected your lunch"
                      badgeText={`${data.Pickup_Data.length} Pickups`}
                      badgeColor="#22A56C"
                      items={data.Pickup_Data.map((p) => ({
                        image: p.Attachment,
                        children: [...new Set(p.Child_Name || [])],
                        time: p.Created_At,
                      }))}
                      isLast={!hasDelivery}
                    />
                  ) : (
                    <StepCard step={2} status="active" Icon={Bike}
                      title="Waiting for Pickup" subtitle="Agent is on the way to collect your lunch"
                      badgeText="Pending" badgeColor="#D4AF37" isLast={!hasDelivery} />
                  )}
                  {hasDelivery ? (
                    <StepCard
                      step={3}
                      status="done"
                      Icon={School}
                      title="Delivered to School"
                      subtitle="Lunch delivered successfully"
                      badgeText={`${data.Delivery_Update.length} Deliveries`}
                      badgeColor="#22A56C"
                      items={data.Delivery_Update.map((d) => ({
                        image: d.Attachment,
                        children: [
                          `${d.School_Name} · ${d.Branch_Name}`
                        ],
                        time: d.Created_At,
                      }))}
                      isLast
                    />
                  ) : (
                    <StepCard step={3} status={deliveryStatus} Icon={School}
                      title="Delivery to School"
                      subtitle={hasPickup ? "Agent is heading to school" : "Waiting for pickup first"}
                      badgeText={hasPickup ? "In Transit" : "Pending"}
                      badgeColor={hasPickup ? "#D4AF37" : "#94A3B8"} isLast />
                  )}
                </div>
              </>
            )}
          </div>

          {/* History panel */}
          <div className="md:w-1/2 flex flex-col border-t md:border-t-0 border-gray-200 relative overflow-hidden">
            <div className="overflow-y-auto flex-1">
              <HistoryCalendar selectedDate={selectedDay?.Date ?? null} onDaySelect={setSelectedDay} />
            </div>
            {selectedDay && <DayDetail day={selectedDay} onClose={() => setSelectedDay(null)} />}
          </div>
        </div>
      ) : (
        /* ─── Full today's tracking ─── */
        <div className="pb-12 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex flex-col items-center justify-center pt-20 gap-3">
              <div className="w-10 h-10 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-slate-500">Fetching tracking info…</p>
            </div>
          ) : error ? (
            <ErrorState onRetry={() => loadData()} />
          ) : !data ? (
            <NotStartedState />
          ) : (
            <>
              <LiveMap selectedBoy={selectedBoy} />
              <ProgressBar hasPickup={hasPickup} hasDelivery={hasDelivery} />
              <div className="px-4 pt-4 pb-1">
                <StepCard step={1} status="done" Icon={UtensilsCrossed}
                  title={hasPickup ? 'Lunch Marked Ready' : 'Mark Lunch as Ready'}
                  subtitle={hasPickup ? "You notified the delivery team" : false}
                  badgeText={hasPickup ? "Prepared" : 'Preparing'} badgeColor="#22A56C" />
                {hasPickup ? (
                  <StepCard
                    step={2}
                    status="done"
                    Icon={Bike}
                    title="Pickup Done"
                    subtitle="Agent has collected your lunch"
                    badgeText={`${data.Pickup_Data.length} Pickups`}
                    badgeColor="#22A56C"
                    items={data.Pickup_Data.map((p) => ({
                      image: p.Attachment,
                      children: [...new Set(p.Child_Name || [])],
                      time: p.Created_At,
                    }))}
                    isLast={!hasDelivery}
                  />
                ) : (
                  <StepCard step={2} status="active" Icon={Bike}
                    title="Waiting for Pickup" subtitle="Agent is on the way to collect your lunch"
                    badgeText="Pending" badgeColor="#D4AF37" isLast={!hasDelivery} />
                )}
                {hasDelivery ? (
                  <StepCard
                    step={3}
                    status="done"
                    Icon={School}
                    title="Delivered to School"
                    subtitle="Lunch delivered successfully"
                    badgeText={`${data.Delivery_Update.length} Deliveries`}
                    badgeColor="#22A56C"
                    items={data.Delivery_Update.map((d) => ({
                      image: d.Attachment,
                      children: [
                        `${d.School_Name} · ${d.Branch_Name}`
                      ],
                      time: d.Created_At,
                    }))}
                    isLast
                  />
                ) : (
                  <StepCard step={3} status={deliveryStatus} Icon={School}
                    title="Delivery to School"
                    subtitle={hasPickup ? "Agent is heading to school" : "Waiting for pickup first"}
                    badgeText={hasPickup ? "In Transit" : "Pending"}
                    badgeColor={hasPickup ? "#D4AF37" : "#94A3B8"} isLast />
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
